import { useMutation, useQuery } from '@tanstack/react-query';
import { transcriptPlayerKeys } from 'app/queries/transcriptPlayer/transcriptPlayerKeys';
import { createFetchTranscriptQueryFn } from 'app/queries/transcriptPlayer/createFetchTranscriptQueryFn';
import { AudioPlayer, AudioPlayerHandle } from '@components/AudioPlayer';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Select } from '@components/core-ui/form-elements/Select';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useAPI } from '@providers/useAPI';
import { handleDownloadError, triggerBrowserDownload } from '@helpers/jobLinkHelpers';
import { MediaAdminService, MediaService } from 'app/API';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { usePageContext } from '../../providers/PageProvider';
import { buildTranscriptComparison, CompareTranscriptLayout } from './components/CompareTranscriptLayout';
import { SplitTranscriptLayout } from './components/SplitTranscriptLayout';
import { TranscriptLayout } from './components/TranscriptLayout';
import { Wrapper } from './styles/Wrapper';

export const LLM_TRANSCRIPT_MODIFIED_FILENAME = 'llm-transcript-modified.json';
export const LLM_TRANSCRIPT_REDIARIZED_FILENAME = 'llm-transcript-rediarized.json';
export const LLM_TRANSCRIPT_ISSUE_FIXED_FILENAME = 'llm_transcript_issue_fixed_transcript.json';
const DEFAULT_COMPARE_MAX_COLOR_TIME_DELTA_MS = 2000;
type TTranscriptViewMode = 'transcript' | 'compare' | 'split';
const COMPARE_SUPPORTED_TRANSCRIPT_FILES = new Set([
  'asr.json',
  'transcript.json',
  'transcript_original.json',
  'llm-transcript.json',
  LLM_TRANSCRIPT_ISSUE_FIXED_FILENAME,
]);

const TRANSCRIPT_FILE_LABELS: Record<string, string> = {
  'asr.json': 'Post ASR',
  'transcript.json': 'ASOne Realtime Transcript',
  'transcript_original.json': 'ASOne Original Transcript',
  'llm-transcript.json': 'Enhanced Transcript',
  [LLM_TRANSCRIPT_MODIFIED_FILENAME]: 'LLM Modified Transcript',
  [LLM_TRANSCRIPT_REDIARIZED_FILENAME]: 'LLM Rediarized Transcript',
  [LLM_TRANSCRIPT_ISSUE_FIXED_FILENAME]: 'LLM Fixed Transcript',
};

const getTranscriptLabel = (filename: string) => TRANSCRIPT_FILE_LABELS[filename] ?? filename;
const isCompareSupportedTranscriptFile = (filename: string) => COMPARE_SUPPORTED_TRANSCRIPT_FILES.has(filename);

export const Content = () => {
  const pageContext = usePageContext();
  const {
    isLoading,
    utterances,
    transcriptFile,
    setTranscriptFile,
    audioSource,
    audioError,
    error,
    jobId,
    asrModel,
    refreshAudioUrl,
    triggerManualReload,
  } = pageContext;
  const { availableTranscripts } = pageContext;
  const [playbackTime, setPlaybackTime] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const audioPlayerRef = useRef<AudioPlayerHandle>(null);
  const audioRetryCountRef = useRef(0);
  const [hasAudioPlaybackFailed, setHasAudioPlaybackFailed] = useState(false);
  const [downloadingAudioJobIds, setDownloadingAudioJobIds] = useState<ReadonlySet<string>>(new Set<string>());
  const [mode, setMode] = useState<TTranscriptViewMode>('transcript');
  const [compareAgainstFile, setCompareAgainstFile] = useState('');
  const [compareMaxColorTimeDeltaMs, setCompareMaxColorTimeDeltaMs] = useState(DEFAULT_COMPARE_MAX_COLOR_TIME_DELTA_MS);
  const [followPlayScroll, setFollowPlayScroll] = useState(false);
  const [searchFocusSignal, setSearchFocusSignal] = useState(0);
  const { call } = useAPI();

  const transcriptFiles = availableTranscripts.includes(transcriptFile)
    ? availableTranscripts
    : [transcriptFile, ...availableTranscripts.filter((file) => file !== transcriptFile)].filter(Boolean);
  const compareCandidateFiles = transcriptFiles.filter(
    (file) => file !== transcriptFile && isCompareSupportedTranscriptFile(file),
  );
  const canCompare = isCompareSupportedTranscriptFile(transcriptFile) && compareCandidateFiles.length > 0;
  const transcriptOptions = availableTranscripts.map((file) => ({
    label: getTranscriptLabel(file),
    value: file,
  }));
  const compareOptions = compareCandidateFiles.map((file) => ({
    label: getTranscriptLabel(file),
    value: file,
  }));

  const seekTo = (timestamp: number) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.seekTo(timestamp);
    }
  };

  const handleAudioError = () => {
    if (audioRetryCountRef.current === 0) {
      audioRetryCountRef.current += 1;
      refreshAudioUrl();
      return;
    }

    setHasAudioPlaybackFailed(true);
  };

  const handleAudioReady = () => {
    audioRetryCountRef.current = 0;
    setHasAudioPlaybackFailed(false);
  };

  const isDownloadingAudio = downloadingAudioJobIds.has(jobId);

  const handleAudioDownload = async () => {
    const signed = await call(() =>
      MediaAdminService.adminGetSignedUrl({ jobId, file: 'output.mp3', disposition: 'attachment' }),
    );
    if (!signed?.signedUrl) {
      throw new Error('No signed URL');
    }
    triggerBrowserDownload(signed.signedUrl, 'output.mp3');
  };

  const handleStandaloneAudioDownload = async () => {
    if (isDownloadingAudio) {
      return;
    }

    const requestJobId = jobId;
    setDownloadingAudioJobIds((current) => new Set(current).add(requestJobId));
    try {
      await handleAudioDownload();
    } catch (error) {
      handleDownloadError(error);
    } finally {
      setDownloadingAudioJobIds((current) => {
        const next = new Set(current);
        next.delete(requestJobId);
        return next;
      });
    }
  };

  useEffect(() => {
    audioRetryCountRef.current = 0;
    setHasAudioPlaybackFailed(false);
  }, [jobId]);

  useEffect(() => {
    setPlaybackTime(0);
  }, [audioSource?.attempt, jobId]);

  useEffect(() => {
    const handleSearchShortcut = (keyboardEvent: KeyboardEvent) => {
      if (
        !(keyboardEvent.metaKey || keyboardEvent.ctrlKey) ||
        keyboardEvent.altKey ||
        keyboardEvent.key.toLowerCase() !== 'f'
      ) {
        return;
      }

      keyboardEvent.preventDefault();
      setSearchFocusSignal((signal) => signal + 1);
    };

    window.addEventListener('keydown', handleSearchShortcut);

    return () => {
      window.removeEventListener('keydown', handleSearchShortcut);
    };
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const toolbar = toolbarRef.current;

    if (!wrapper || !toolbar) {
      return;
    }

    const updateToolbarBottom = () => {
      const toolbarRect = toolbar.getBoundingClientRect();
      wrapper.style.setProperty(
        '--transcript-toolbar-bottom',
        window.matchMedia('(min-width: 1280px)').matches ? `${toolbarRect.bottom}px` : '0px',
      );
      wrapper.style.setProperty(
        '--transcript-toolbar-clearance',
        window.matchMedia('(min-width: 1280px)').matches ? `${toolbarRect.height}px` : '0px',
      );
    };

    updateToolbarBottom();

    const resizeObserver = new ResizeObserver(updateToolbarBottom);
    resizeObserver.observe(toolbar);
    window.addEventListener('resize', updateToolbarBottom);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateToolbarBottom);
      wrapper.style.removeProperty('--transcript-toolbar-bottom');
      wrapper.style.removeProperty('--transcript-toolbar-clearance');
    };
  }, [mode]);

  useEffect(() => {
    if (!canCompare) {
      if (mode !== 'transcript') {
        setMode('transcript');
      }
      setCompareAgainstFile('');
      return;
    }

    if (
      !compareAgainstFile ||
      compareAgainstFile === transcriptFile ||
      !compareCandidateFiles.includes(compareAgainstFile)
    ) {
      setCompareAgainstFile(compareCandidateFiles[0]);
    }
  }, [canCompare, compareAgainstFile, compareCandidateFiles, mode, transcriptFile]);

  const comparisonQuery = useQuery({
    queryKey: transcriptPlayerKeys.detail(jobId, compareAgainstFile),
    queryFn: createFetchTranscriptQueryFn(call),
    enabled:
      (mode === 'compare' || mode === 'split') && Boolean(compareAgainstFile) && !error && !isLoading && canCompare,
  });
  const compareSourceUtterances = useMemo(() => comparisonQuery.data?.utterances ?? [], [comparisonQuery.data]);
  const compareUtterances = useMemo(
    () => (mode === 'compare' ? buildTranscriptComparison(utterances, compareSourceUtterances) : []),
    [mode, utterances, compareSourceUtterances],
  );
  const compareError = comparisonQuery.isError ? 'Unable to load the comparison transcript' : null;
  const isCompareLoading = comparisonQuery.isLoading;

  const replaceTranscript = useMutation({
    mutationFn: async (file: File) => {
      const params = await call(() => MediaService.generateUploadParams({ jobId, file: transcriptFile }), {
        onError: () => {},
      });
      if (!params?.url || !params?.fields) {
        throw new Error('Failed to get upload parameters');
      }
      const formData = new FormData();
      Object.entries(params.fields).forEach(([key, value]) => formData.append(key, String(value)));
      formData.append('file', file);
      const response = await fetch(params.url, { method: 'PUT', body: formData });
      if (!response.ok) {
        throw new Error('File upload error on S3');
      }
    },
    onSuccess: () => {
      toast.success('File replaced successfully!');
      triggerManualReload();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'File upload error'),
  });
  const isUploading = replaceTranscript.isPending;

  const handleReplaceClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) {
      return;
    }

    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    replaceTranscript.mutate(file);
  };

  return (
    <Wrapper ref={wrapperRef}>
      {(isLoading || isCompareLoading) && <LinearProgressLoader active={isLoading || isCompareLoading} />}

      <div
        ref={toolbarRef}
        className="relative xl:fixed bg-[var(--color-surface)] p-4 shadow-md w-full xl:w-[calc(100%-300px)] z-10 mb-4 xl:mb-10"
      >
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <div className="w-40 shrink-0">
              <Select
                name="mode"
                label="Mode"
                value={mode}
                disabled={isLoading || !canCompare}
                onChange={(_label, value) => setMode(value === 'compare' || value === 'split' ? value : 'transcript')}
                options={
                  canCompare
                    ? [
                        {
                          label: 'Transcript',
                          value: 'transcript',
                        },
                        {
                          label: 'Compare against',
                          value: 'compare',
                        },
                        {
                          label: 'Split pane',
                          value: 'split',
                        },
                      ]
                    : [
                        {
                          label: 'Transcript',
                          value: 'transcript',
                        },
                      ]
                }
                required={false}
              />
            </div>

            <div className="w-56 shrink-0">
              <Select
                name="transcriptFile"
                label="Transcript File"
                value={transcriptFile}
                disabled={isLoading}
                onChange={(_label, value) => setTranscriptFile(value)}
                options={transcriptOptions}
                required={false}
              />
            </div>
            {asrModel ? (
              <div className="flex h-10 min-w-36 max-w-52 shrink-0 flex-col justify-center rounded-md bg-[var(--color-surface-muted)] px-3 text-xs text-[var(--color-text-muted)]">
                <span className="block font-medium uppercase">ASR model</span>
                <span className="block truncate font-semibold text-[var(--color-text-subtle)]" title={asrModel}>
                  {asrModel}
                </span>
              </div>
            ) : null}
          </div>

          <div className="flex min-w-0 w-full xl:w-auto flex-wrap items-center justify-center gap-3">
            <div className="w-full min-w-0 xl:w-[clamp(320px,34vw,520px)] xl:shrink-0 pt-[10px]">
              {audioError || hasAudioPlaybackFailed ? (
                <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
                  <span>Audio unavailable</span>
                  <button
                    type="button"
                    disabled={isDownloadingAudio}
                    className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-title)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-[var(--color-text-muted)]"
                    onClick={() => void handleStandaloneAudioDownload()}
                  >
                    Download audio
                  </button>
                </div>
              ) : audioSource ? (
                <AudioPlayer
                  key={`${jobId}:${audioSource.attempt}`}
                  src={audioSource.url}
                  ref={audioPlayerRef}
                  downloadFileName="output.mp3"
                  onDownload={handleAudioDownload}
                  onError={handleAudioError}
                  onReady={handleAudioReady}
                  onPlay={(timestamp) => {
                    setPlaybackTime(timestamp);
                  }}
                />
              ) : null}
            </div>

            <div
              className="cursor-pointer rounded-md bg-[var(--color-surface-muted)] p-2 text-[8px] uppercase text-[var(--color-text-muted)]"
              onClick={handleReplaceClick}
              style={{ opacity: isUploading ? 0.5 : 1, pointerEvents: isUploading ? 'none' : 'auto' }}
            >
              {isUploading ? 'Uploading...' : 'replace'}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </div>
          </div>

          <div className="flex min-w-0">
            {(mode === 'compare' || mode === 'split') && canCompare ? (
              <div className="w-56">
                <Select
                  name="compareAgainstFile"
                  label="Compare against"
                  value={compareAgainstFile}
                  disabled={isLoading || isCompareLoading}
                  onChange={(_label, value) => setCompareAgainstFile(value)}
                  options={compareOptions}
                  required={false}
                />
              </div>
            ) : null}
          </div>
        </div>

        {mode === 'compare' && canCompare ? (
          <div className="mt-3 flex items-center gap-3">
            <label
              htmlFor="compare-max-color-time-delta"
              className="min-w-40 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]"
            >
              Max Diff Color
            </label>

            <input
              id="compare-max-color-time-delta"
              type="range"
              min={0}
              max={10000}
              step={100}
              value={compareMaxColorTimeDeltaMs}
              disabled={isLoading || isCompareLoading}
              onChange={(event) => setCompareMaxColorTimeDeltaMs(Number(event.target.value))}
              className="flex-1 accent-sky-600"
            />

            <span className="w-20 text-right font-mono text-xs text-[var(--color-text-subtle)]">
              {compareMaxColorTimeDeltaMs} ms
            </span>
          </div>
        ) : null}

        {mode === 'split' && canCompare ? (
          <div className="mt-3 flex items-center gap-3">
            <span className="min-w-40 text-xs font-medium uppercase tracking-wide text-slate-500">Follow Play</span>
            <Switch checked={followPlayScroll} onChange={() => setFollowPlayScroll((value) => !value)} />
            <span className="text-xs text-slate-500">Keep both panes centered on the highlighted word</span>
          </div>
        ) : null}

        {!isLoading && !canCompare ? (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            Compare and split modes require two transcripts with word timings.
          </p>
        ) : null}
      </div>

      {error ? (
        <div className="pt-[100px]">
          <ErrorMessage error={error} />
        </div>
      ) : null}

      {!error && (mode === 'compare' || mode === 'split') && compareError ? (
        <div className="pt-[100px]">
          <ErrorMessage error={compareError} />
        </div>
      ) : null}

      {!isLoading && !error && mode === 'transcript' && utterances.length > 0 && (
        <TranscriptLayout
          utterances={utterances}
          playbackTime={playbackTime}
          onWordClick={(timestamp) => seekTo(timestamp / 1000)}
          searchNamePrefix="transcript"
          searchFocusSignal={searchFocusSignal}
        />
      )}

      {!isLoading &&
        !error &&
        mode === 'compare' &&
        !compareError &&
        !isCompareLoading &&
        compareUtterances.length > 0 && (
          <CompareTranscriptLayout
            utterances={compareUtterances}
            playbackTime={playbackTime}
            onWordClick={(timestamp) => seekTo(timestamp / 1000)}
            maxColorTimeDeltaMs={compareMaxColorTimeDeltaMs}
            searchFocusSignal={searchFocusSignal}
          />
        )}

      {!isLoading &&
        !error &&
        mode === 'split' &&
        !compareError &&
        !isCompareLoading &&
        compareSourceUtterances.length > 0 && (
          <SplitTranscriptLayout
            primaryUtterances={utterances}
            compareUtterances={compareSourceUtterances}
            primaryTitle={getTranscriptLabel(transcriptFile)}
            compareTitle={getTranscriptLabel(compareAgainstFile)}
            playbackTime={playbackTime}
            onWordClick={(timestamp) => seekTo(timestamp / 1000)}
            followPlayScroll={followPlayScroll}
            searchFocusSignal={searchFocusSignal}
          />
        )}
    </Wrapper>
  );
};
